/** Wait until route content and visible images are ready, then hide loaders. */

export function removeBootLoader() {
  const boot = document.getElementById('boot-loader');
  if (!boot) return;
  boot.style.opacity = '0';
  boot.style.transition = 'opacity 0.25s ease';
  window.setTimeout(() => boot.remove(), 260);
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function imageHasSrc(img) {
  const src = img.currentSrc || img.getAttribute('src') || '';
  return Boolean(src) && !src.startsWith('data:');
}

function isImageLoaded(img) {
  return img.complete && img.naturalHeight > 0;
}

function isRoughlyVisible(img) {
  const rect = img.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight + 120;
}

/**
 * Resolves when above-the-fold (or first) images with src have loaded,
 * or when timeout is reached. Also waits briefly for DOM content to appear.
 */
export async function waitForPageReady({
  root = document.getElementById('root'),
  minMs = 400,
  maxMs = 12000,
  domWaitMs = 6000,
} = {}) {
  if (!root) return;

  const started = Date.now();

  // 1) Wait until route content has mounted into the DOM
  const domDeadline = Date.now() + domWaitMs;
  while (Date.now() < domDeadline) {
    const hasRoute =
      root.querySelector('.app, main, .auth-page, .hero, .header, img[src]');
    if (hasRoute) break;
    await sleep(40);
  }

  // Give React one frame to attach img nodes
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  // 2) Wait for visible images that already have a src (skip lazy placeholders)
  const collectTargets = () => {
    const withSrc = [...root.querySelectorAll('img')].filter(imageHasSrc);
    const visible = withSrc.filter(isRoughlyVisible);
    return (visible.length ? visible : withSrc.slice(0, 10)).filter((img) => !isImageLoaded(img));
  };

  let pending = collectTargets();
  // Lazy images may gain src shortly after mount — recheck a few times
  for (let i = 0; i < 8 && pending.length === 0; i += 1) {
    await sleep(60);
    pending = collectTargets();
    if ([...root.querySelectorAll('img')].some((img) => imageHasSrc(img) && isRoughlyVisible(img) && isImageLoaded(img))) {
      break;
    }
  }

  if (pending.length) {
    const remaining = Math.max(0, maxMs - (Date.now() - started));
    await Promise.race([
      Promise.all(
        pending.map(
          (img) =>
            new Promise((resolve) => {
              if (isImageLoaded(img)) {
                resolve();
                return;
              }
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
        )
      ),
      sleep(remaining),
    ]);
  }

  // 3) Respect a short minimum so the loader doesn't flash
  const elapsed = Date.now() - started;
  if (elapsed < minMs) {
    await sleep(minMs - elapsed);
  }
}
